import { useContext, useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BoxFull,
  Button,
  Dropdown,
  TransactionalSubheader,
} from "iuvity-ui-components";
import { CustomInput, Header } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { useConfigurations } from "../../hooks/useConfigurations";
import { useVersions } from "../../hooks/useVersions";
import { useWidgets } from "../../hooks/useWidgets";
import { Version, Widget } from "../../interfaces";
import { REVALIDATE_FORM_PROPS } from "../../constants";
import {
  getConfigurationContext,
  getContextFromKey,
  getGlobalConfigurationPageLabel,
} from "../../utils";
import { ConfigurationsFormSchema } from "../../validations/schemas";
import { AppContext } from "../../context/index";
import { MultiSelect } from "primereact/multiselect";

interface NewConfigurationProps {
  update?: boolean;
}

const formBlankValues = {
  key: "",
  widgets: [],
  description: "",
  type: "",
  value: "",
  status: "INACTIVE",
  releaseversion: "",
};

export const NewConfigurationPage: React.FC<NewConfigurationProps> = ({
  update = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    t,
    appState: { user, selectedConfig },
  } = useContext(AppContext);

  const pageName = location?.pathname.split("/").at(-2) || "";

  const { configurationAction } = useConfigurations("ACTIONS");
  const { data: versionsData } = useVersions("DATA");
  const { data: widgetsData } = useWidgets(
    ["administrative", "globalConfiguration"].includes(pageName)
      ? "DATA"
      : "ACTIONS"
  );

  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const customPageLabel = getGlobalConfigurationPageLabel(
    t,
    location.pathname.substring(0, location.pathname.lastIndexOf("/"))
  );

  const {
    control,
    getValues,
    setValue,
    formState: { errors, isValid, isDirty },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: update
      ? {
          id: selectedConfig?.id,
          key: selectedConfig?.key.split(".").pop(),
          widgets: selectedConfig?.widgets,
          description: selectedConfig?.description,
          type: selectedConfig?.type,
          value: selectedConfig?.value,
          status: selectedConfig?.status,
          releaseversion: selectedConfig?.releaseversion,
          updatedby: user?.name,
        }
      : {
          ...formBlankValues,
          createdby: user?.name,
        },
  });

  const configTypeWatcher = useWatch({
    control,
    name: "type",
  });

  const contextLabel = `default.${getConfigurationContext(
    location.pathname,
    true
  )}`;

  const typeOptions = useMemo(
    () => [
      {
        label: t("GlobalConfigurations.textOptionLabel"),
        value: "text",
      },
      {
        label: t("GlobalConfigurations.numericOptionLabel"),
        value: "number",
      },
      {
        label: t("GlobalConfigurations.booleanOptionLabel"),
        value: "boolean",
      },
    ],
    [t]
  );

  const onReturn = () => {
    navigate(-1);
    window.scrollTo(0, 0);
  };

  const onAction = async () => {
    const { key, ...rest } = getValues();
    const newKey = `${contextLabel}.${key}`;
    await configurationAction(update ? "UPDATE" : "CREATE", [
      {
        ...rest,
        key: newKey,
      },
    ]);
    navigate(`/configurations/${getContextFromKey(newKey || "")}`);
    return window.scrollTo(0, 0);
  };

  const handleModalProps = (actionType: "action" | "cancel" | "cancelProcess") => {
    switch (actionType) {
      case "action":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t(
            `GlobalConfigurations.${update ? "save" : "create"}ModalTitle`
          ),
          content: t(
            `GlobalConfigurations.${update ? "save" : "create"}ModalContent`
          ),
          onConfirm: onAction,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("GlobalConfigurations.cancelModalTitle"),
          content: t("GlobalConfigurations.cancelModalContent"),
          onConfirm: onReturn,
        }));
      case "cancelProcess":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("GlobalConfigurations.cancelProcessModalTitle"),
          content: t("GlobalConfigurations.cancelProcessModalContent"),
          onConfirm: onReturn,
        }));
      default:
        return;
    }
  };

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}

      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={onReturn}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {update
              ? t("GlobalConfigurations.editTitle")
              : t("GlobalConfigurations.newTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("GlobalConfigurations.breadcrumbParentLabel")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: customPageLabel,
              command: () => onReturn(),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: update
                ? t("GlobalConfigurations.editTitle")
                : t("GlobalConfigurations.newTitle"),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      <BoxFull
        className="flex align-items-center justify-content-center my-4"
        variant="tertiary"
      >
        <div className="form-container md py-5">
          <div className="form md">
            <CustomInput
              id={`input-label`}
              className="rd-only mb-3"
              label={t("GlobalConfigurations.labelkeyLabel")}
              type="text"
              autoComplete="off"
              value={contextLabel}
              readOnly
            />

            <Controller
              name="key"
              control={control}
              rules={ConfigurationsFormSchema(t).key}
              render={({ field }) => {
                const isCustom =
                  update && !selectedConfig?.iscustomconfiguration;
                return (
                  <>
                    <CustomInput
                      id={`input-key`}
                      label={t("GlobalConfigurations.keyLabel")}
                      placeholder={t(
                        "GlobalConfigurations.keyPlaceholderLabel"
                      )}
                      type="text"
                      autoComplete="off"
                      className={`${isCustom ? "rd-only" : ""} ${
                        errors.key?.message ? "p-invalid" : "mb-4"
                      }`}
                      style={{ width: "20rem" }}
                      maxLength={60}
                      keyfilter={/^[A-Za-z0-9]*$/}
                      readOnly={isCustom}
                      {...field}
                    />
                  </>
                );
              }}
            />
            <ErrorMessage
              errors={errors}
              name="key"
              //@ts-ignore
              render={({ message }) => (
                <small
                  className="p-error block"
                  style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
                >
                  {message}
                </small>
              )}
            />

            {["administrative", "globalConfiguration"].includes(pageName) &&
              widgetsData && (
                <Controller
                  name="widgets"
                  control={control}
                  // rules={ConfigurationsFormSchema(t)?.widgets}
                  render={({ field }) => {
                    field.onChange = (e: any): void => {
                      setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
                    };
                    return (
                      <>
                        <label
                          htmlFor={"widgets"}
                          className="block lb mt-2 mb-1"
                        >
                          {t(`GlobalConfigurations.widgetsLabel`)}
                        </label>
                        {/* @ts-ignore */}
                        <MultiSelect
                          id={`select-widget`}
                          display="chip"
                          placeholder={t(
                            `GlobalConfigurations.widgetsPlaceholder`
                          )}
                          className="mb-4"
                          options={(widgetsData || [])
                            .map((item: Widget) => ({
                              label: item?.widgetname,
                              value: item?.widgetname,
                            }))
                            .sort((a: any, b: any) =>
                              a?.label.localeCompare(b?.label)
                            )}
                          filter
                          emptyFilterMessage={t(
                            "GlobalConfigurations.noAvailableMessage"
                          )}
                          {...field}
                        />
                      </>
                    );
                  }}
                />
              )}

            <Controller
              name="description"
              control={control}
              rules={ConfigurationsFormSchema(t).description}
              render={({ field }) => {
                return (
                  <>
                    <CustomInput
                      id={`input-description`}
                      label={t("GlobalConfigurations.descriptionLabel")}
                      placeholder={t(
                        "GlobalConfigurations.descriptionPlaceholderLabel"
                      )}
                      type="text"
                      autoComplete="off"
                      className={`${errors.description?.message ? "p-invalid" : "mb-4"}`}
                      style={{ width: "20rem" }}
                      maxLength={512}
                      keyfilter={
                        /^[^-+'"*;|?&=<>()%{}:@\u002F\u005B\u005C\u005D]+$/
                      }
                      {...field}
                    />
                  </>
                );
              }}
            />
            <ErrorMessage
              errors={errors}
              name="description"
              //@ts-ignore
              render={({ message }) => (
                <small
                  className="p-error block"
                  style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
                >
                  {message}
                </small>
              )}
            />

            <Controller
              name="type"
              control={control}
              rules={ConfigurationsFormSchema(t).type}
              render={({ field }) => {
                field.onChange = (e) => {
                  // @ts-ignore
                  const newValue = e.target.value;
                  setValue(field.name, newValue, REVALIDATE_FORM_PROPS);
                  if (
                    !(
                      (getValues("value") || "").length > 0 &&
                      newValue !== "boolean"
                    ) ||
                    (newValue !== "boolean" &&
                      ["false", "true"].includes(getValues("value") || ""))
                  )
                    setValue("value", newValue === "boolean" ? "false" : "");
                };
                return (
                  <>
                    {/* @ts-ignore */}
                    <Dropdown
                      label={t("GlobalConfigurations.typeLabel")}
                      placeholder={t(
                        "GlobalConfigurations.typePlaceholderLabel"
                      )}
                      className="mb-4"
                      width="20rem"
                      options={typeOptions}
                      filter={false}
                      showClear={false}
                      {...field}
                    />
                  </>
                );
              }}
            />

            {configTypeWatcher === "boolean" ? (
              <Controller
                key={`c-${configTypeWatcher}`}
                name="value"
                control={control}
                rules={ConfigurationsFormSchema(t).value}
                render={({ field }) => {
                  field.onChange = (e) => {
                    const newValue =
                      // @ts-ignore
                      e.target.value === true ? "true" : "false";
                    setValue(field.name, newValue, REVALIDATE_FORM_PROPS);
                  };
                  return (
                    <CustomInput
                      id={`input-value`}
                      label={t("GlobalConfigurations.valueLabel")}
                      placeholder={t(
                        "GlobalConfigurations.valuePlaceholderLabel"
                      )}
                      type="boolean"
                      autoComplete="off"
                      className={`${errors.value?.message ? "p-invalid" : "mb-4"}`}
                      style={{ width: "20rem" }}
                      maxLength={2048}
                      {...field}
                    />
                  );
                }}
              />
            ) : (
              <>
                <Controller
                  key={`c-${configTypeWatcher}`}
                  name="value"
                  control={control}
                  rules={ConfigurationsFormSchema(t).value}
                  render={({ field }) => {
                    return (
                      <CustomInput
                        id={`input-value`}
                        label={t("GlobalConfigurations.valueLabel")}
                        placeholder={t(
                          "GlobalConfigurations.valuePlaceholderLabel"
                        )}
                        type="text"
                        autoComplete="off"
                        className={`${errors.value?.message ? "p-invalid" : "mb-4"}`}
                        style={{ width: "20rem" }}
                        maxLength={2048}
                        {...field}
                      />
                    );
                  }}
                />
                <ErrorMessage
                  errors={errors}
                  name="value"
                  //@ts-ignore
                  render={({ message }) => (
                    <small
                      className="p-error block"
                      style={{
                        marginTop: "0.5rem",
                        marginBottom: "1.5rem",
                      }}
                    >
                      {message}
                    </small>
                  )}
                />
              </>
            )}

            <Controller
              name="status"
              control={control}
              rules={ConfigurationsFormSchema(t).status}
              render={({ field: { value, ...field } }) => {
                field.onChange = (e) => {
                  const newValue =
                    // @ts-ignore
                    e.target.value === true ? "ACTIVE" : "INACTIVE";
                  setValue("status", newValue, REVALIDATE_FORM_PROPS);
                };
                return (
                  <CustomInput
                    id={`input-status`}
                    label={t("GlobalConfigurations.statusLabel")}
                    type="boolean"
                    autoComplete="off"
                    className="mb-4"
                    style={{ width: "20rem" }}
                    maxLength={2048}
                    value={getValues("status") === "ACTIVE"}
                    {...field}
                  />
                );
              }}
            />

            {versionsData && (
              <Controller
                name="releaseversion"
                control={control}
                rules={ConfigurationsFormSchema(t).releaseversion}
                render={({ field }) => {
                  return (
                    <>
                      {/* @ts-ignore */}
                      <Dropdown
                        label={t("GlobalConfigurations.versionLabel")}
                        placeholder={t(
                          "GlobalConfigurations.versionPlaceholderLabel"
                        )}
                        className="mb-4"
                        width="20rem"
                        options={versionsData
                          .map((item: Version) => ({
                            label: item?.name,
                            value: item?.name,
                          }))
                          .sort((a: any, b: any) =>
                            b?.label.localeCompare(a?.label)
                          )}
                        filter={false}
                        showClear={false}
                        {...field}
                      />
                    </>
                  );
                }}
              />
            )}
          </div>
        </div>
      </BoxFull>

      <BoxFull className="my-5 p-0" style={{ backgroundColor: "transparent" }}>
        <div className="flex align-items-center justify-content-end gap-5">
          <Button
            variant="p-button-link"
            label={
              update
              ? t("GlobalConfigurations.cancelBtnLabel")
              : t("GlobalConfigurations.returnBtnLabel")
            }
            onClick={
              update ? isDirty ? () => handleModalProps("cancel") : () => handleModalProps("cancelProcess") : onReturn
            }
          />
          <Button
            variant="p-button"
            label={
              update
                ? t("GlobalConfigurations.saveBtnLabel")
                : t("GlobalConfigurations.createBtnLabel")
            }
            disabled={update ? !(isValid && isDirty) : !isValid}
            onClick={() => handleModalProps("action")}
          />
        </div>
      </BoxFull>

      <CustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
